import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  type AlexandriaCard as TCard,
  type AlexandriaSection,
  useAlexandriaSections,
} from "../lib/useAlexandriaSections";
import { useTritonHandoff } from "../lib/triton-handoff";
import type {
  ConnectionStatus,
  EnvelopeFromClient,
  EnvelopeFromServer,
} from "../lib/aide-protocol";

// Per LS-SPEC-27 Alexandria storefront. Real card render — backed by
// /v1/alexandria/sections served by lifestyle-bff over the edge.
// Empty state still shows when there are no crawled records yet.

interface AlexandriaStoreProps {
  wsStatus: ConnectionStatus;
  send: (envelope: EnvelopeFromClient) => void;
  envelopes: EnvelopeFromServer[];
}

export function AlexandriaStore({ wsStatus, send, envelopes }: AlexandriaStoreProps) {
  const consumerId = useAuth((s) => s.consumerId);
  const { status, data, error, refresh } = useAlexandriaSections(consumerId);
  // Sections that just received a live update — surfaced as a pulse.
  const [pulseSections, setPulseSections] = useState<Set<string>>(new Set());
  const lastSeenIndex = useRef(0);

  // Subscribe to Alexandria live updates once the WS is open. Unsubscribe
  // on unmount (or when the WS state changes away from open).
  useEffect(() => {
    if (wsStatus !== "open") return;
    send({ type: "alexandria.subscribe", sections: ["for-you", "watch-results", "recent-activity"] });
    return () => {
      send({ type: "alexandria.unsubscribe" });
    };
  }, [wsStatus, send]);

  // Watch envelopes for alexandria.section_update — refresh sections +
  // pulse the affected section so the consumer notices the change.
  useEffect(() => {
    const fresh = envelopes.slice(lastSeenIndex.current);
    lastSeenIndex.current = envelopes.length;
    const updatedSections = new Set<string>();
    for (const env of fresh) {
      if (env.type === "alexandria.section_update") {
        updatedSections.add(env.section);
      }
    }
    if (updatedSections.size > 0) {
      refresh();
      setPulseSections((prev) => new Set([...prev, ...updatedSections]));
      // Clear the pulse after a beat so it doesn't linger.
      const timer = setTimeout(() => {
        setPulseSections((prev) => {
          const next = new Set(prev);
          for (const s of updatedSections) next.delete(s);
          return next;
        });
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [envelopes, refresh]);

  return (
    <div className="px-4 pt-3 pb-6">
      <header className="mb-4 flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-medium text-(--color-ink)">Your DealBook</h1>
          <p className="mt-1 text-xs text-(--color-ink-faint)">
            Sealed deals + a curated storefront — only what your scouts and crawlers brought back.
          </p>
        </div>
        {data && (
          <span className="text-[10px] text-(--color-ink-faint)">
            {data.total_crawled_records} record{data.total_crawled_records === 1 ? "" : "s"} crawled
          </span>
        )}
      </header>

      {status === "loading" && (
        <p className="text-xs text-(--color-ink-faint)">Loading your storefront…</p>
      )}
      {status === "error" && (
        <div className="rounded-(--radius-card) border border-red-900/50 bg-red-950/30 p-3 text-xs text-red-300">
          <p>Couldn't reach Alexandria: {error}</p>
          <button
            onClick={refresh}
            className="mt-2 rounded-(--radius-pill) bg-(--color-canvas-elev) px-3 py-1 text-(--color-ink-muted)"
          >
            Try again
          </button>
        </div>
      )}

      {data && (
        <div className="space-y-4">
          {data.sections.map((section) => (
            <SectionView
              key={section.id}
              section={section}
              pulsing={pulseSections.has(section.id)}
            />
          ))}
        </div>
      )}

      <p className="mt-6 text-center text-[11px] text-(--color-ink-faint)">
        Have a specific ask?{" "}
        <Link
          to="/triton"
          className="text-(--color-accent-gold) underline-offset-2 hover:underline"
        >
          Tell Triton
        </Link>
        .
      </p>
    </div>
  );
}


function SectionView({
  section,
  pulsing,
}: {
  section: AlexandriaSection;
  pulsing: boolean;
}) {
  return (
    <section
      aria-labelledby={`section-${section.id}`}
      className={
        "rounded-(--radius-card) border bg-(--color-canvas-card) p-4 transition-colors " +
        (pulsing
          ? "border-(--color-accent-gold)/60"
          : "border-white/5")
      }
    >
      <div className="flex items-center gap-2">
        <h2
          id={`section-${section.id}`}
          className="text-sm font-medium text-(--color-ink)"
        >
          {section.title}
        </h2>
        {pulsing && (
          <span
            aria-live="polite"
            className="rounded-(--radius-pill) bg-(--color-accent-gold)/15 px-1.5 py-0.5 text-[10px] text-(--color-accent-gold) animate-pulse"
          >
            Fresh
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-(--color-ink-muted)">{section.blurb}</p>
      {section.cards.length === 0 ? (
        <EmptyCard sectionTitle={section.title} />
      ) : (
        <ul className="mt-3 space-y-2">
          {section.cards.map((card) => (
            <li key={card.id}>
              <CardView card={card} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


function CardView({ card }: { card: TCard }) {
  const navigate = useNavigate();
  const setPendingCard = useTritonHandoff((s) => s.setPendingCard);

  function handleAction(actionId: string) {
    if (actionId === "ask_triton") {
      setPendingCard(card);
      navigate("/triton");
    } else if (actionId === "open_source" && card.source_url) {
      window.open(card.source_url, "_blank", "noopener,noreferrer");
    } else if (actionId === "dispatch_scout") {
      // POC: dispatch_scout currently routes through Triton — the user
      // tells Triton "watch for X like this card" and Aide picks up.
      setPendingCard(card);
      navigate("/triton");
    }
  }

  // Whole card surface is clickable — opens the source page on a
  // primary click anywhere on the body. The action buttons below
  // still work for "Ask Triton" / "Send a scout".
  function onCardClick(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest("button")) return;
    if (card.source_url) {
      window.open(card.source_url, "_blank", "noopener,noreferrer");
    }
  }
  return (
    <div
      className="cursor-pointer rounded-xl border border-white/5 bg-(--color-canvas-elev) p-3 transition-colors hover:bg-(--color-canvas-card)"
      onClick={onCardClick}
      role="button"
      tabIndex={0}
      aria-label={`Open ${card.title} on source`}
    >
      <div className="flex gap-3">
        {card.image_url ? (
          <img
            src={card.image_url}
            alt=""
            loading="lazy"
            className="h-16 w-16 flex-shrink-0 rounded-lg object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        ) : (
          <div className="grid h-16 w-16 flex-shrink-0 place-items-center rounded-lg bg-(--color-canvas-card) text-(--color-accent-gold-dim)">
            <CardTypeIcon vertical={card.vertical} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium text-(--color-ink) truncate">{card.title}</h3>
          <p className="mt-0.5 text-[11px] text-(--color-ink-muted) truncate">
            {card.subtitle}
          </p>
          {card.geography_label && (
            <p className="text-[10px] text-(--color-ink-faint) truncate">
              {card.geography_label}
            </p>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {card.actions.map((action) => (
          <button
            key={action.id}
            onClick={() => handleAction(action.id)}
            className={
              action.id === "ask_triton"
                ? "rounded-(--radius-pill) bg-(--color-accent-gold) px-2.5 py-1 text-[11px] font-medium text-stone-900"
                : "rounded-(--radius-pill) bg-(--color-canvas-card) px-2.5 py-1 text-[11px] text-(--color-ink-muted) hover:text-(--color-ink)"
            }
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}


function CardTypeIcon({ vertical }: { vertical: string }) {
  // Minimal glyph per vertical — keeps the empty-image card identifiable
  // without pulling an icon library.
  if (vertical === "food") return <span aria-hidden="true">🍽</span>;
  if (vertical === "wellness") return <span aria-hidden="true">✦</span>;
  if (vertical === "travel.hotel") return <span aria-hidden="true">⌂</span>;
  if (vertical === "travel.package") return <span aria-hidden="true">✈</span>;
  if (vertical === "fashion") return <span aria-hidden="true">◆</span>;
  return <span aria-hidden="true">◌</span>;
}


function EmptyCard({ sectionTitle }: { sectionTitle: string }) {
  return (
    <div className="mt-3 flex items-center gap-3 rounded-xl border border-dashed border-white/10 px-3 py-3 text-xs text-(--color-ink-faint)">
      <span
        aria-hidden="true"
        className="grid h-9 w-9 place-items-center rounded-full bg-(--color-canvas-elev) text-(--color-accent-gold-dim)"
      >
        ◌
      </span>
      <div className="flex-1">
        <p className="text-(--color-ink-muted)">No {sectionTitle.toLowerCase()} yet.</p>
        <p>Your scouts will fill this in.</p>
      </div>
    </div>
  );
}
